import React, { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { createPublicClient, http, formatEther } from 'viem';
import { pulsechainV4 } from 'wagmi/chains';
import contractRaffle from '../contractData/contractRaffle.json';
import { Link } from 'react-router-dom';
import defaultImage from '../assets/logo.webp'; // Add a default image
import { useLocation, useNavigate } from 'react-router-dom';

const address = contractRaffle.address;
const ABI = contractRaffle.abi;

const publicClient = createPublicClient({
  chain: pulsechainV4,
  transport: http()
});

const ReportedRaffles = () => {
  const { address: walletAddress } = useAccount();
  const [reportedRaffles, setReportedRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
const { state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReportedRaffles = async () => {
      if (!walletAddress) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Get detailed reported raffles information
        const [raffleData, imageUrls, reportCounts] = await publicClient.readContract({
          address,
          abi: ABI,
          functionName: 'getReportedRaffleDetails',
          args: [walletAddress]
        });

        // Transform data for easier use in the table
        const rafflesData = raffleData.map((raffle, index) => ({
          id: Number(raffle.raffleId),
          name: raffle.name,
          tagline: raffle.tagline,
          creator: raffle.creator,
          startTime: Number(raffle.startTime),
          endTime: Number(raffle.endTime),
          isStopped: raffle.isStopped,
          totalTicketsIssued: Number(raffle.totalTicketsIssued),
          totalValue: Number(raffle.totalValue),
          reportCount: Number(reportCounts[index]),
          ticketPrice: Number(raffle.ticketPrice),
          winner: raffle.winner,
          winningTicketIndex: Number(raffle.winningTicketIndex),
          imageUrl: imageUrls[index]
        }));

        setReportedRaffles(rafflesData);
      } catch (err) {
        console.error("Error fetching reported raffles:", err);
        setError("Failed to load reported raffles. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchReportedRaffles();
  }, [walletAddress]);

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const formatStatus = (isStopped, endTime) => {
    const now = Math.floor(Date.now() / 1000);
    if (isStopped) return 'Completed';
    if (endTime < now) return 'Ended (Pending Winner)';
    return 'Active';
  };

  if (!walletAddress) {
    return (
      <div className="connect-wallet-message">
        <h2>Reported Raffles</h2>
        <p>Please connect your wallet to view your reported raffles.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading your reported raffles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Reported Raffles</h2>
        <p className="error-message">{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (reportedRaffles.length === 0) {
    return (
      <div className="no-raffles-container">
        <h2>Your Reported Raffles</h2>
        <p>You haven't reported any raffles yet.</p>
        <Link to="/raffles" className="browse-raffles-link">
          Browse Raffles
        </Link>
      </div>
    );
  }

  return (
    <div className="reported-raffles-container">
      <h2>My Reported Raffles</h2>
      <p className="subtitle">
        You've reported {reportedRaffles.length} raffle{reportedRaffles.length !== 1 ? 's' : ''}
      </p>

      <div className="table-responsive">
        <table className="reported-raffles-table">
          <thead>
            <tr>
              <th>Raffle</th>
              <th>Details</th>
              <th>Creator</th>
              <th>Status</th>
              <th>Reports</th>
              <th>Value</th>
             </tr>
          </thead>
          <tbody>
            {reportedRaffles.map((raffle) => (
              <tr key={raffle.id}>
                <td className="raffle-info-cell">
                  <div className="raffle-image-container">
                    <img 
                      src={raffle.imageUrl || defaultImage} 
                      alt={raffle.name} 
                      className="raffle-thumbnail"
                      onError={(e) => {
                        e.target.onerror = null; 
                        e.target.src = defaultImage;
                      }}
                    />
                  </div>
                  <div className="raffle-text-info">
                    <h3 className="raffle-name">{raffle.name}</h3>
                    <p className="raffle-name">ID: {raffle.id}</p>
                    <p className="raffle-tagline">{raffle.tagline}</p>
                  </div>
                </td>
                <td>
                  <div className="raffle-details">
                    <p><strong>Start:</strong> {formatDate(raffle.startTime)}</p>
                    <p><strong>End:</strong> {formatDate(raffle.endTime)}</p>
                    <p><strong>Tickets:</strong> {raffle.totalTicketsIssued}</p>
                    {raffle.isStopped && raffle.winner !== '0x0000000000000000000000000000000000000000' && (
                      <p className="winner-info">
                        <strong>Winner:</strong> {formatAddress(raffle.winner)}
                      </p>
                    )}
                  </div>
                </td>
                <td>
                  <a 
                    href={`https://scan.v4.testnet.pulsechain.com/#/address/${raffle.creator}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="address-link"
                  >
                    {formatAddress(raffle.creator)}
                  </a>
                </td>
                <td>
                  <span className={`status-badge ${formatStatus(raffle.isStopped, raffle.endTime).toLowerCase().replace(/\s+/g, '-')}`}>
                    {formatStatus(raffle.isStopped, raffle.endTime)}
                  </span>
                </td>
                <td>
                  <div className="report-count">
                    {raffle.reportCount} report{raffle.reportCount !== 1 ? 's' : ''}
                  </div>
                </td>
                <td>
                  {formatEther(raffle.totalValue.toString())} PLS
                </td>
           
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportedRaffles;